import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { StoreHandlerProps } from "@store/Store/HandlerCreator";
import { QuoteLineToCartModel } from "@store/Types/QuoteToCart";
import cloneDeep from "lodash/cloneDeep";

type Parameter = {
    productIds: string[];
    quoteLines: QuoteLineToCartModel[];
    onSegmentComplete?: (step:string, nbComplete?:number, nbProcessed?:number, nbTotal?:number) => void;
};

type Props = StoreHandlerProps & {
    productIds: string[];
    quoteLines: QuoteLineToCartModel[];
};

type HandlerType = Handler<Parameter, Props>;

export const DispatchBeginAddProduct: HandlerType = props => {
    props.dispatch({
        type: "Pages/QuickOrder/BeginAddProduct",
    });
};

export const CopyCurrentValues: HandlerType = props => {
    props.quoteLines = cloneDeep(props.parameter.quoteLines);
    props.productIds = cloneDeep(props.parameter.productIds);
};

export const ChangeProductQtyForExistingProduct: HandlerType = props => {
    props.quoteLines.forEach(quoteLine => {
        const productInfo: ProductInfo = {
            productId: quoteLine.productId,
            unitOfMeasure: quoteLine.unitOfMeasure,
            qtyOrdered: quoteLine.quantityOrdered,
            productDetailPath: "",
        };
        quoteLine.productInfo = productInfo;
        const quickOrderProductInfos = props.getState().store.pages.quickOrder.productInfos; // Poolcorp Custom Code
        const { productId, qtyOrdered, unitOfMeasure } = productInfo;
        const existingProductInfo = quickOrderProductInfos.find(
            o => o.productId === productId && (o.unitOfMeasure === unitOfMeasure || unitOfMeasure === ""),
        );
        if (!existingProductInfo) {
            return;
        }

        productInfo.qtyOrdered = existingProductInfo.qtyOrdered + qtyOrdered;
    });
};

export const GetPrice: HandlerType = async props => {
    const productPriceParameters: ProductInfo[] = [];
    props?.quoteLines
        .map(quoteLine => {
            return quoteLine.productInfo;
        })
        .forEach(productInfo => {
            if (productInfo) {
                productPriceParameters.push(productInfo);
            }
        });

    let loadedPricing = false;

    props.parameter.onSegmentComplete?.("ImportQuote_PricesLoading");
    props.dispatch(
        loadRealTimePricing({
            productPriceParameters,
            onComplete: realTimePricingProps => {
                if (realTimePricingProps.apiResult) {
                    props?.quoteLines.forEach(quoteLine => {
                        const { productId, productInfo } = quoteLine;
                        if (productInfo) {
                            productInfo.pricing = realTimePricingProps?.apiResult?.realTimePricingResults?.find(
                                o => o.productId === productId,
                            );
                        } else if (realTimePricingProps.error) {
                            productInfo!.failedToLoadPricing = true;
                        }
                    });
                }

                loadedPricing = true;
            },
        }),
    );

    await waitFor(() => loadedPricing === true);
};

export const GetInventory: HandlerType = async props => {
    let loadedAllInventory = false;
    const size = 25;
    const nbChunks = Math.ceil(props.productIds.length/size);
    let nbLoaded = 0;

    props.parameter.onSegmentComplete?.("ImportQuote_InventoryLoading");
    for (let i = 0; i < props.productIds.length; i += size) {
        let loadedInventory = false;
        const productIdsChunk = props.productIds.slice(i, i + size);

        props.dispatch(
            loadRealTimeInventory({
                productIds: productIdsChunk,
                expand: ["warehouses"],
                onSuccess: result => {
                    if (result?.realTimeInventoryResults) {
                        props?.quoteLines
                        .filter(ql => productIdsChunk.includes(ql.productId))
                        .forEach(quoteLine => {
                            const { productId, productInfo } = quoteLine;
                            if (productInfo) {
                                productInfo.inventory = result?.realTimeInventoryResults?.find(
                                    o => o.productId === productId,
                                );
                            }
                        });
                    }
                    nbLoaded++;
                    if(nbLoaded >= nbChunks) {
                        loadedAllInventory = true;
                    }
                    loadedInventory = true;
                },
                onError: result => {
                    props?.quoteLines
                    .filter(ql => productIdsChunk.includes(ql.productId))
                    .forEach(quoteLine => {
                        const { productInfo } = quoteLine;
                        if (productInfo) {
                            productInfo.failedToLoadInventory = true;
                        }
                    });
                    nbLoaded++;
                    if(nbLoaded >= nbChunks) {
                        loadedAllInventory = true;
                    }
                    loadedInventory = true;
                }
            }),
        );

        await waitFor(() => loadedInventory === true);
    }

    await waitFor(() => loadedAllInventory === true);
};

export const DispatchCompleteAddProduct: HandlerType = props => {
    props.dispatch({
        type: "Store/Pages/QuickOrder/CompleteAddProducts",
        productInfos: props?.quoteLines.map(ql => ql.productInfo),
    });
};

export const chain = [
    DispatchBeginAddProduct,
    CopyCurrentValues,
    ChangeProductQtyForExistingProduct,
    GetPrice,
    GetInventory,
    DispatchCompleteAddProduct,
];

const addProductFromQuote = createHandlerChainRunner(chain, "AddProductFromQuote");
export default addProductFromQuote;
